import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Search, Calendar, Clock, User, Filter, X, ChevronLeft, ChevronsLeft, ChevronRight, ChevronsRight, FileDown, FileText, Trash2, Printer, Edit, History, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, List } from 'lucide-react';
import { serviceCallsAPI, clientsAPI, usersAPI, serviceCallTypesAPI, serviceCallVisitsAPI, cspireAPI } from '@shared/apiClient';
import {
  formatDate,
  formatDateTime,
  getStatusColor,
  getStatusText,
  getResolutionDisplay,
  formatCurrency,
  getServiceCallListCustomerName,
  formatCoolingType
} from '@shared/utils';
import toast from 'react-hot-toast';
import { downloadInvoicePdfForServiceCall, printInvoiceForServiceCall } from '../utils/invoiceActions';
import { generateServiceCallPDF, printServiceCallPDF } from '../utils/pdfGenerator';
import CspireInvoiceEditor from '../components/CspireInvoiceEditor';
import InventorySearch from '../components/InventorySearch';
import ReturnToTechModal from '../components/ReturnToTechModal';
import ServiceCallListExportModal from '../components/ServiceCallListExportModal';
import CopyTextButton from '../components/CopyTextButton';

// Service Call Edit Modal Component
export const ServiceCallEditModal = ({ serviceCall, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    workPerformed: '',
    partsUsed: [],
    laborHours: '',
    totalCost: '',
    customerNotes: '',
    invoiceNumber: '',
    status: 'assigned',
    completedDate: '',
    generatorHours: '',
    mileage: '',
    typeId: '',
    isWarranty: false,
    isCorrectionFlag: false,
    correctionNote: '',
    correctionAtFaultTechnicianId: '',
    oilType: '',
    oilAmount: ''
  });
  const [initialData, setInitialData] = useState(null);
  const [newPart, setNewPart] = useState({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [useCustomPart, setUseCustomPart] = useState(false);
  const queryClient = useQueryClient();

  // Fetch full service call details to ensure all data is loaded
  const { data: serviceCallData, isLoading: isLoadingServiceCall } = useQuery(
    ['serviceCall-edit', serviceCall?.id],
    () => serviceCallsAPI.getServiceCall(serviceCall.id),
    { enabled: isOpen && !!serviceCall }
  );

  // Fetch service call types
  const { data: typesData } = useQuery(
    'serviceCallTypes-edit',
    () => serviceCallTypesAPI.getServiceCallTypes({ includeInactive: false }),
    { enabled: isOpen }
  );

  const serviceCallTypes = typesData?.data?.types || [];

  const { data: techniciansEditData } = useQuery(
    'technicians-edit-modal',
    () => usersAPI.getUsers({ role: 'technician', limit: 200 }),
    { enabled: isOpen }
  );
  const techniciansForCorrection = techniciansEditData?.data?.users || [];
  
  // Use the loaded data if available, otherwise fall back to the passed serviceCall
  const fullServiceCall = serviceCallData?.data?.serviceCall;

  useEffect(() => {
    // Only populate form when we have the full service call data loaded
    if (fullServiceCall && !isLoadingServiceCall) {
      console.log('Populating form with service call data:', fullServiceCall);
      
      // Get the most recent visit data if visits exist
      const visits = fullServiceCall.visits || [];
      const mostRecentVisit = visits.length > 0 ? visits[visits.length - 1] : null;
      
      console.log('Most recent visit:', mostRecentVisit);
      
      // Use visit data if available, otherwise fall back to service call data
      const cspireSiteForPrefill = fullServiceCall.cspireWorkOrder?.site || null;
      const data = {
        workPerformed: mostRecentVisit?.workPerformed || fullServiceCall.workPerformed || '',
        partsUsed: Array.isArray(mostRecentVisit?.partsUsed) ? mostRecentVisit.partsUsed : 
                   Array.isArray(fullServiceCall.partsUsed) ? fullServiceCall.partsUsed : [],
        laborHours: mostRecentVisit?.laborHours ? String(mostRecentVisit.laborHours) : 
                   fullServiceCall.laborHours ? String(fullServiceCall.laborHours) : '',
        totalCost: fullServiceCall.totalCost ? String(fullServiceCall.totalCost) : '',
        customerNotes: fullServiceCall.customerNotes || '',
        invoiceNumber: fullServiceCall.invoiceNumber || '',
        status: fullServiceCall.status || 'assigned',
        completedDate: fullServiceCall.completedDate ? new Date(fullServiceCall.completedDate).toISOString().split('T')[0] : '',
        generatorHours: mostRecentVisit?.generatorHours ? String(mostRecentVisit.generatorHours) : 
                       fullServiceCall.generatorHours ? String(fullServiceCall.generatorHours) : '',
        mileage: mostRecentVisit?.mileage ? String(mostRecentVisit.mileage) : 
                fullServiceCall.mileage ? String(fullServiceCall.mileage) : '',
        typeId: mostRecentVisit?.typeId || fullServiceCall.typeId || '',
        isWarranty: Boolean(fullServiceCall.isWarranty),
        isCorrectionFlag: Boolean(fullServiceCall.isCorrectionFlag),
        correctionNote: fullServiceCall.correctionNote || '',
        correctionAtFaultTechnicianId: fullServiceCall.correctionAtFaultTechnicianId || '',
        oilType: cspireSiteForPrefill?.oilType || '',
        oilAmount: cspireSiteForPrefill?.oilAmount || ''
      };
      
      console.log('Setting form data:', data);
      setFormData(data);
      setInitialData(data);
    }
  }, [fullServiceCall, isLoadingServiceCall]);

  const updateMutation = useMutation(
    (data) => serviceCallsAPI.updateServiceCall(serviceCall.id, data),
    {
      onSuccess: () => {
        toast.success('Service call updated successfully!');
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
        queryClient.invalidateQueries(['serviceCall-edit', serviceCall.id]);
        queryClient.invalidateQueries('techMetrics');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update service call');
      }
    }
  );

  const handleInventorySelect = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem);
    setNewPart({
      inventoryId: inventoryItem.inventoryId || '',
      itemNumber: inventoryItem.itemNumber,
      description: inventoryItem.description,
      name: inventoryItem.itemNumber,
      quantity: inventoryItem.quantity || 1,
      cost: inventoryItem.cost || 0
    });
    setUseCustomPart(false);
  };

  const handleAddPart = () => {
    if (useCustomPart) {
      if (newPart.name && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            itemNumber: newPart.name,
            description: newPart.description || newPart.name,
            name: newPart.name,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
      } else {
        toast.error('Please enter part name, quantity, and cost');
      }
    } else {
      if (newPart.itemNumber && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            ...(newPart.inventoryId ? { inventoryId: newPart.inventoryId } : {}),
            itemNumber: newPart.itemNumber,
            description: newPart.description || newPart.name,
            name: newPart.itemNumber,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
      } else {
        toast.error('Please select a part and enter quantity');
      }
    }
  };

  const handleToggleMode = () => {
    setUseCustomPart(!useCustomPart);
    setSelectedInventoryItem(null);
    setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  };

  const handleRemovePart = (index) => {
    setFormData({
      ...formData,
      partsUsed: formData.partsUsed.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.isCorrectionFlag && !formData.correctionAtFaultTechnicianId) {
      toast.error('Please select the at-fault technician');
      return;
    }
    
    // Fields that live on the CSpire site (not on the service call) are
    // collected separately and pushed via the CSpire admin endpoint.
    const cspireSiteOnlyFields = new Set(['oilType', 'oilAmount']);

    // Only send changed fields
    const updateData = {};
    const cspireSiteUpdate = {};

    Object.keys(formData).forEach(key => {
      if (JSON.stringify(formData[key]) !== JSON.stringify(initialData[key])) {
        if (cspireSiteOnlyFields.has(key)) {
          const trimmed = typeof formData[key] === 'string' ? formData[key].trim() : formData[key];
          cspireSiteUpdate[key] = trimmed === '' ? null : trimmed;
        } else if (key === 'partsUsed') {
          updateData[key] = formData[key].length > 0 ? formData[key] : null;
        } else if (['laborHours', 'totalCost', 'generatorHours', 'mileage'].includes(key)) {
          updateData[key] = formData[key] ? parseFloat(formData[key]) : null;
        } else if (key === 'isWarranty' || key === 'isCorrectionFlag') {
          updateData[key] = formData[key];
        } else if (key === 'correctionNote') {
          updateData[key] = formData.isCorrectionFlag ? (formData[key] || null) : null;
        } else if (key === 'correctionAtFaultTechnicianId') {
          updateData[key] = formData.isCorrectionFlag ? (formData[key] || null) : null;
        } else if (key === 'completedDate') {
          updateData[key] = formData[key] || null;
        } else if (key === 'invoiceNumber') {
          updateData[key] = formData[key] || null;
        } else {
          updateData[key] = formData[key];
        }
      }
    });

    const cspireSiteId = fullServiceCall?.cspireWorkOrder?.site?.id;
    const hasCspireSiteChanges = Object.keys(cspireSiteUpdate).length > 0 && cspireSiteId;

    if (Object.keys(updateData).length === 0 && !hasCspireSiteChanges) {
      toast.info('No changes to save');
      return;
    }

    // When parts or work performed change, also sync to the most recent visit
    // so visit-level displays and invoice PDF pick up the update (the edit
    // form reads these fields from the visit, not the service call).
    const visits = fullServiceCall?.visits || [];
    const mostRecentVisit = visits.length > 0 ? visits[visits.length - 1] : null;
    const visitSyncFields = {};
    if (updateData.partsUsed !== undefined) visitSyncFields.partsUsed = updateData.partsUsed;
    if (updateData.workPerformed !== undefined) visitSyncFields.workPerformed = updateData.workPerformed;
    if (Object.keys(visitSyncFields).length > 0 && mostRecentVisit) {
      serviceCallVisitsAPI.updateVisit(serviceCall.id, mostRecentVisit.id, visitSyncFields)
        .catch((err) => console.error('Failed to sync to visit:', err));
    }

    // Push CSpire site equipment updates (oil type/amount) in parallel.
    // Failures here are surfaced but don't block the service-call save.
    if (hasCspireSiteChanges) {
      cspireAPI.updateSite(cspireSiteId, cspireSiteUpdate)
        .then(() => {
          queryClient.invalidateQueries('cspireSites');
          queryClient.invalidateQueries('cspireWorkOrders');
          queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
          queryClient.invalidateQueries(['serviceCall-edit', serviceCall.id]);
        })
        .catch((err) => {
          console.error('Failed to update CSpire site oil fields:', err);
          toast.error(err.response?.data?.message || 'Failed to save CSpire oil type/amount');
        });
    }

    if (Object.keys(updateData).length === 0) {
      // Only CSpire-site changes — no service-call mutation needed; close once update fires.
      if (hasCspireSiteChanges) {
        toast.success('CSpire site oil info saved');
        onClose();
      }
      return;
    }

    updateMutation.mutate(updateData);
  };

  if (!isOpen) return null;

  // Show loading state while fetching full service call data
  if (isLoadingServiceCall || !fullServiceCall) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading service call data...</p>
        </div>
      </div>
    );
  }

  const client = fullServiceCall.client;
  const isCspire = !!fullServiceCall.isCspire;
  const isFranklin = !!fullServiceCall.isFranklin;
  const franklinSite = fullServiceCall.franklinSite;
  const cspireWorkOrder = fullServiceCall.cspireWorkOrder;
  const cspireSite = cspireWorkOrder?.site;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-4 pb-4 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium text-gray-900">
            Edit Service Call
            {isCspire && (cspireSite?.siteId || cspireSite?.locationName || cspireWorkOrder?.workOrderNumber) && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                — {[
                  cspireSite?.siteId && `Site #${cspireSite.siteId}`,
                  cspireSite?.locationName,
                  cspireWorkOrder?.workOrderNumber && `WO# ${cspireWorkOrder.workOrderNumber}`
                ].filter(Boolean).join(' · ')}
              </span>
            )}
            {isFranklin && (franklinSite?.siteId || franklinSite?.locationName) && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                — {[
                  franklinSite?.siteId && `Site #${franklinSite.siteId}`,
                  franklinSite?.locationName
                ].filter(Boolean).join(' · ')}
              </span>
            )}
          </h3>
          <div className="flex items-center space-x-2">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={updateMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onClose}
              type="button"
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Editing service call:</span> The form below is pre-populated with existing data. Modify any fields you need to update.
            </p>
          </div>

          {/* Client Info (Read-only) */}
          <div className="bg-gray-50 p-3 rounded-md">
            {isCspire ? (
              <>
                <p className="text-sm font-medium text-gray-700">
                  <span className="font-semibold">Customer:</span> CSpire
                  {cspireSite?.siteId && <span className="ml-1 text-gray-500">— Site #{cspireSite.siteId}</span>}
                  {cspireSite?.locationName && <span className="ml-1 text-gray-500">({cspireSite.locationName})</span>}
                </p>
                {cspireSite?.addressLine1 && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-semibold">Address:</span> {cspireSite.addressLine1}{cspireSite.city ? `, ${cspireSite.city}` : ''}{cspireSite.state ? `, ${cspireSite.state}` : ''} {cspireSite.zip || ''}
                  </p>
                )}
                {(cspireWorkOrder?.openedByName || cspireWorkOrder?.openedByPhone) && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-semibold">Contact:</span> {cspireWorkOrder.openedByName || ''} {cspireWorkOrder.openedByPhone ? `(${cspireWorkOrder.openedByPhone})` : ''}
                  </p>
                )}
              </>
            ) : isFranklin && franklinSite ? (
              <>
                <p className="text-sm font-medium text-gray-700">
                  <span className="font-semibold">Customer:</span> Franklin
                  {franklinSite.siteId && <span className="ml-1 text-gray-500">— Site #{franklinSite.siteId}</span>}
                  {franklinSite.locationName && <span className="ml-1 text-gray-500">({franklinSite.locationName})</span>}
                </p>
                {franklinSite.company && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-semibold">Company:</span> {franklinSite.company}
                  </p>
                )}
                {franklinSite.address && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-semibold">Address:</span> {franklinSite.address}{franklinSite.city ? `, ${franklinSite.city}` : ''}{franklinSite.state ? `, ${franklinSite.state}` : ''} {franklinSite.zipCode ? ` ${franklinSite.zipCode}` : ''}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700">
                  <span className="font-semibold">Customer:</span> {client?.companyName || 'N/A'}
                </p>
                {client && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className="font-semibold">Contact:</span> {client.contactFirstName} {client.contactLastName}
                  </p>
                )}
              </>
            )}
            {fullServiceCall && fullServiceCall.technician && (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Assigned Technician:</span> {fullServiceCall.technician.firstName} {fullServiceCall.technician.lastName}
              </p>
            )}
          </div>

          {/* Invoice Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Number
            </label>
            <input
              type="text"
              value={formData.invoiceNumber}
              onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter invoice number..."
            />
          </div>

          {/* Service Type and Warranty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Type
              </label>
              <select
                value={formData.typeId}
                onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select type...</option>
                {serviceCallTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isWarranty"
                checked={formData.isWarranty}
                onChange={(e) => setFormData({ ...formData, isWarranty: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isWarranty" className="ml-2 text-sm font-medium text-gray-700">
                Warranty Service
              </label>
            </div>
          </div>

          {/* Correction Flag */}
          <div className={`border rounded-md p-3 ${formData.isCorrectionFlag ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isCorrectionFlag"
                checked={formData.isCorrectionFlag}
                onChange={(e) => setFormData({
                  ...formData,
                  isCorrectionFlag: e.target.checked,
                  correctionNote: e.target.checked ? formData.correctionNote : '',
                  correctionAtFaultTechnicianId: e.target.checked ? formData.correctionAtFaultTechnicianId : ''
                })}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <label htmlFor="isCorrectionFlag" className="ml-2 text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Correction Flag
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">Mark if this call resulted from a prior technician mistake</p>
            {formData.isCorrectionFlag && (
              <div className="mt-2 ml-6 space-y-2">
                <div>
                  <label htmlFor="correctionAtFaultTechnicianId" className="block text-sm font-medium text-amber-900 mb-1">
                    At-fault technician
                  </label>
                  <select
                    id="correctionAtFaultTechnicianId"
                    value={formData.correctionAtFaultTechnicianId}
                    onChange={(e) => setFormData({ ...formData, correctionAtFaultTechnicianId: e.target.value })}
                    className="w-full px-3 py-2 border border-amber-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 text-sm"
                    required
                  >
                    <option value="">Select technician...</option>
                    {(() => {
                      const byId = new Map(techniciansForCorrection.map((t) => [t.id, t]));
                      const af = fullServiceCall.correctionAtFaultTechnician;
                      if (formData.correctionAtFaultTechnicianId && !byId.has(formData.correctionAtFaultTechnicianId) && af?.id) {
                        byId.set(af.id, af);
                      }
                      return Array.from(byId.values())
                        .filter((t) => t.isActive !== false || t.id === formData.correctionAtFaultTechnicianId)
                        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`))
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.firstName} {t.lastName}
                            {!t.isActive ? ' (inactive)' : ''}
                          </option>
                        ));
                    })()}
                  </select>
                  <p className="text-xs text-amber-700 mt-1">Who made the mistake that required this correction visit?</p>
                </div>
                <textarea
                  value={formData.correctionNote}
                  onChange={(e) => setFormData({ ...formData, correctionNote: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 text-sm"
                  placeholder="What went wrong? (optional)"
                />
              </div>
            )}
          </div>

          {/* Completed Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Completed Date
            </label>
            <input
              type="date"
              value={formData.completedDate}
              onChange={(e) => setFormData({ ...formData, completedDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Work Performed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Work Performed
            </label>
            <textarea
              value={formData.workPerformed}
              onChange={(e) => setFormData({ ...formData, workPerformed: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe the work performed..."
            />
          </div>

          {/* Parts Used */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parts Used
            </label>
            {formData.partsUsed.length > 0 && (
              <div className="mb-2 space-y-2">
                {formData.partsUsed.map((part, index) => {
                  const itemNumber = part.itemNumber || part.name || 'N/A';
                  const description = part.description || part.name || '';
                  const quantity = parseFloat(part.quantity) || 0;
                  const cost = parseFloat(part.cost) || 0;
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{itemNumber}</div>
                        {description && <div className="text-xs text-gray-600">{description}</div>}
                        <div className="text-xs text-gray-500 mt-1">
                          Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePart(index)}
                        className="text-red-600 hover:text-red-800 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600">
                  {useCustomPart ? 'Custom Part Mode' : 'Inventory Search Mode'}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMode}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {useCustomPart ? 'Search Inventory Instead' : 'Add Custom Part Instead'}
                </button>
              </div>

              {useCustomPart ? (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Part Name/Number *</label>
                    <input
                      type="text"
                      placeholder="Enter part name or number"
                      value={newPart.name}
                      onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Description (optional)</label>
                    <input
                      type="text"
                      placeholder="Enter part description"
                      value={newPart.description}
                      onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Cost *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!newPart.name || !newPart.quantity || !newPart.cost}
                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <InventorySearch 
                    onSelect={handleInventorySelect}
                    placeholder="Search for parts by item number or description..."
                  />
                  {selectedInventoryItem && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-sm">
                      <div className="font-medium text-blue-900">{selectedInventoryItem.itemNumber}</div>
                      <div className="text-blue-700">{selectedInventoryItem.description}</div>
                      <div className="text-blue-600 mt-1">Cost: ${parseFloat(selectedInventoryItem.cost).toFixed(2)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Cost (override)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={selectedInventoryItem ? `$${parseFloat(selectedInventoryItem.cost).toFixed(2)}` : "Cost"}
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!selectedInventoryItem || !newPart.quantity}
                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Service Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Labor Hours
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.laborHours}
                onChange={(e) => setFormData({ ...formData, laborHours: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Cost
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.totalCost}
                onChange={(e) => setFormData({ ...formData, totalCost: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Generator Hours
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.generatorHours}
                onChange={(e) => setFormData({ ...formData, generatorHours: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.mileage}
                onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* CSpire Site - Oil (lives on the CSpire site, not on the service call) */}
          {isCspire && cspireSite && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-xs font-semibold text-amber-900 mb-2">
                CSpire Site Oil Spec
                <span className="ml-1 font-normal text-amber-700">— saved on the site, not the service call</span>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Oil Type
                  </label>
                  <input
                    type="text"
                    value={formData.oilType}
                    onChange={(e) => setFormData({ ...formData, oilType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 15W-40 Synthetic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Oil Amount
                  </label>
                  <input
                    type="text"
                    value={formData.oilAmount}
                    onChange={(e) => setFormData({ ...formData, oilAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 5 quarts"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Customer Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Notes
            </label>
            <textarea
              value={formData.customerNotes}
              onChange={(e) => setFormData({ ...formData, customerNotes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Notes for the customer..."
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="assigned">Assigned</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="return">Return</option>
            </select>
          </div>
        </form>
      </div>
    </div>
  );
};

// Visit Edit Modal Component
const VisitEditModal = ({ visit, serviceCallId, isOpen, onClose, isCspire = false }) => {
  const [formData, setFormData] = useState({
    workPerformed: '',
    partsUsed: [],
    laborHours: '',
    generatorHours: '',
    mileage: '',
    fuelLevel: '',
    typeId: ''
  });
  const [initialData, setInitialData] = useState(null);
  const [newPart, setNewPart] = useState({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [useCustomPart, setUseCustomPart] = useState(false);
  const queryClient = useQueryClient();

  // Fetch service call types
  const { data: typesData } = useQuery(
    'serviceCallTypes-visit-edit',
    () => serviceCallTypesAPI.getServiceCallTypes({ includeInactive: false }),
    { enabled: isOpen }
  );

  const serviceCallTypes = typesData?.data?.types || [];

  useEffect(() => {
    if (visit && isOpen) {
      const data = {
        workPerformed: visit.workPerformed || '',
        partsUsed: Array.isArray(visit.partsUsed) ? visit.partsUsed : [],
        laborHours: visit.laborHours ? String(visit.laborHours) : '',
        generatorHours: visit.generatorHours ? String(visit.generatorHours) : '',
        mileage: visit.mileage ? String(visit.mileage) : '',
        fuelLevel: visit.fuelLevel || '',
        typeId: visit.typeId || ''
      };
      setFormData(data);
      setInitialData(data);
    }
  }, [visit, isOpen]);

  const updateMutation = useMutation(
    (data) => serviceCallVisitsAPI.updateVisit(serviceCallId, visit.id, data),
    {
      onSuccess: () => {
        toast.success('Visit updated successfully!');
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries(['serviceCall', serviceCallId]);
        queryClient.invalidateQueries(['serviceCall-edit', serviceCallId]);
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update visit');
      }
    }
  );

  const handleInventorySelect = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem);
    setNewPart({
      inventoryId: inventoryItem.inventoryId || '',
      itemNumber: inventoryItem.itemNumber,
      description: inventoryItem.description,
      name: inventoryItem.itemNumber,
      quantity: inventoryItem.quantity || 1,
      cost: inventoryItem.cost || 0
    });
    setUseCustomPart(false);
  };

  const handleAddPart = () => {
    if (useCustomPart) {
      if (newPart.name && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            itemNumber: newPart.name,
            description: newPart.description || newPart.name,
            name: newPart.name,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
      } else {
        toast.error('Please enter part name, quantity, and cost');
      }
    } else {
      if (newPart.itemNumber && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            ...(newPart.inventoryId ? { inventoryId: newPart.inventoryId } : {}),
            itemNumber: newPart.itemNumber,
            description: newPart.description || newPart.name,
            name: newPart.itemNumber,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
      } else {
        toast.error('Please select a part and enter quantity');
      }
    }
  };

  const handleToggleMode = () => {
    setUseCustomPart(!useCustomPart);
    setSelectedInventoryItem(null);
    setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  };

  const handleRemovePart = (index) => {
    setFormData({
      ...formData,
      partsUsed: formData.partsUsed.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Only send changed fields
    const updateData = {};
    
    Object.keys(formData).forEach(key => {
      if (JSON.stringify(formData[key]) !== JSON.stringify(initialData[key])) {
        if (key === 'partsUsed') {
          updateData[key] = formData[key].length > 0 ? formData[key] : null;
        } else if (['laborHours', 'generatorHours', 'mileage'].includes(key)) {
          updateData[key] = formData[key] ? parseFloat(formData[key]) : null;
        } else if (key === 'fuelLevel') {
          const trimmed = (formData[key] || '').trim();
          updateData[key] = trimmed === '' ? null : trimmed;
        } else {
          updateData[key] = formData[key];
        }
      }
    });
    
    if (Object.keys(updateData).length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    updateMutation.mutate(updateData);
  };

  if (!isOpen || !visit) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Edit Visit #{visit.visitNumber}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Visit Date:</span> {formatDateTime(visit.visitDate)}
            </p>
            {visit.technician && (
              <p className="text-sm text-blue-800 mt-1">
                <span className="font-semibold">Technician:</span> {visit.technician.firstName} {visit.technician.lastName}
              </p>
            )}
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Type
            </label>
            <select
              value={formData.typeId}
              onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select type...</option>
              {serviceCallTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Work Performed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Work Performed
            </label>
            <textarea
              value={formData.workPerformed}
              onChange={(e) => setFormData({ ...formData, workPerformed: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe the work performed..."
            />
          </div>

          {/* Parts Used */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parts Used
            </label>
            {formData.partsUsed.length > 0 && (
              <div className="mb-2 space-y-2">
                {formData.partsUsed.map((part, index) => {
                  const itemNumber = part.itemNumber || part.name || 'N/A';
                  const description = part.description || part.name || '';
                  const quantity = parseFloat(part.quantity) || 0;
                  const cost = parseFloat(part.cost) || 0;
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{itemNumber}</div>
                        {description && <div className="text-xs text-gray-600">{description}</div>}
                        <div className="text-xs text-gray-500 mt-1">
                          Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePart(index)}
                        className="text-red-600 hover:text-red-800 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600">
                  {useCustomPart ? 'Custom Part Mode' : 'Inventory Search Mode'}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMode}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {useCustomPart ? 'Search Inventory Instead' : 'Add Custom Part Instead'}
                </button>
              </div>

              {useCustomPart ? (
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Part Name/Number *</label>
                    <input
                      type="text"
                      placeholder="Enter part name or number"
                      value={newPart.name}
                      onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Description (optional)</label>
                    <input
                      type="text"
                      placeholder="Enter part description"
                      value={newPart.description}
                      onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Cost *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!newPart.name || !newPart.quantity || !newPart.cost}
                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <InventorySearch 
                    onSelect={handleInventorySelect}
                    placeholder="Search for parts by item number or description..."
                  />
                  {selectedInventoryItem && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-sm">
                      <div className="font-medium text-blue-900">{selectedInventoryItem.itemNumber}</div>
                      <div className="text-blue-700">{selectedInventoryItem.description}</div>
                      <div className="text-blue-600 mt-1">Cost: ${parseFloat(selectedInventoryItem.cost).toFixed(2)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Cost (override)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={selectedInventoryItem ? `$${parseFloat(selectedInventoryItem.cost).toFixed(2)}` : "Cost"}
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!selectedInventoryItem || !newPart.quantity}
                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Service Metrics */}
          <div className={`grid ${isCspire ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3'} gap-4`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Labor Hours
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.laborHours}
                onChange={(e) => setFormData({ ...formData, laborHours: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Generator Hours
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.generatorHours}
                onChange={(e) => setFormData({ ...formData, generatorHours: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.mileage}
                onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
            </div>
            {isCspire && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Level
                </label>
                <input
                  type="text"
                  maxLength={100}
                  value={formData.fuelLevel}
                  onChange={(e) => setFormData({ ...formData, fuelLevel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder='e.g. "3/4 tank"'
                />
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quick inline editor for CSpire site Oil Type / Oil Amount.
// Used inside ServiceCallDetailsModal so admins can update site oil
// info without opening the full Edit Service Call form.
const CspireOilQuickEditPanel = ({ cspireSite, serviceCallId }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [oilType, setOilType] = useState('');
  const [oilAmount, setOilAmount] = useState('');

  useEffect(() => {
    setOilType(cspireSite?.oilType || '');
    setOilAmount(cspireSite?.oilAmount || '');
    setIsEditing(false);
  }, [cspireSite?.id, cspireSite?.oilType, cspireSite?.oilAmount]);

  const saveMutation = useMutation(
    (payload) => cspireAPI.updateSite(cspireSite.id, payload),
    {
      onSuccess: () => {
        toast.success('Oil info saved');
        queryClient.invalidateQueries('cspireSites');
        queryClient.invalidateQueries('cspireWorkOrders');
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries('completedServiceCalls');
        if (serviceCallId) {
          queryClient.invalidateQueries(['serviceCall', serviceCallId]);
          queryClient.invalidateQueries(['serviceCall-edit', serviceCallId]);
        }
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save oil info');
      }
    }
  );

  if (!cspireSite?.id) return null;

  const dash = <span className="text-gray-400">—</span>;

  const handleSave = () => {
    const trimmedType = (oilType || '').trim();
    const trimmedAmount = (oilAmount || '').trim();
    saveMutation.mutate({
      oilType: trimmedType === '' ? null : trimmedType,
      oilAmount: trimmedAmount === '' ? null : trimmedAmount
    });
  };

  const handleCancel = () => {
    setOilType(cspireSite?.oilType || '');
    setOilAmount(cspireSite?.oilAmount || '');
    setIsEditing(false);
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-amber-900">
          CSpire Site Oil
          <span className="ml-1 text-xs font-normal text-amber-700">— saved on the site, applies to all visits</span>
        </h4>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="px-2 py-1 text-xs font-medium bg-white border border-amber-300 text-amber-900 rounded hover:bg-amber-100 flex items-center gap-1"
          >
            <Edit className="h-3 w-3" />
            Edit
          </button>
        )}
      </div>
      {isEditing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Oil Type</label>
              <input
                type="text"
                value={oilType}
                onChange={(e) => setOilType(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 15W-40 Synthetic"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Oil Amount</label>
              <input
                type="text"
                value={oilAmount}
                onChange={(e) => setOilAmount(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 5 quarts"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              disabled={saveMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              className="px-3 py-1 text-xs font-medium text-white bg-amber-600 border border-amber-700 rounded hover:bg-amber-700 disabled:opacity-50"
            >
              {saveMutation.isLoading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-amber-900 font-medium">Oil Type:</span>{' '}
            <span className="text-gray-900">{cspireSite.oilType || dash}</span>
          </div>
          <div>
            <span className="text-amber-900 font-medium">Oil Amount:</span>{' '}
            <span className="text-gray-900">{cspireSite.oilAmount || dash}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Service Call Details Modal Component
export const ServiceCallDetailsModal = ({ serviceCall, isOpen, onClose, onEdit, onReturnToTech }) => {
  const queryClient = useQueryClient();
  const [visitToEdit, setVisitToEdit] = useState(null);
  const [isVisitEditModalOpen, setIsVisitEditModalOpen] = useState(false);
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);

  // Fetch full service call details including visits
  const { data: serviceCallData, isLoading } = useQuery(
    ['serviceCall', serviceCall?.id],
    () => serviceCallsAPI.getServiceCall(serviceCall.id),
    { enabled: isOpen && !!serviceCall }
  );

  const finalizeMutation = useMutation(
    () => serviceCallsAPI.finalizeServiceCall(serviceCall.id),
    {
      onSuccess: () => {
        toast.success('Service call finalized successfully');
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries('completedServiceCalls');
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to finalize service call');
      }
    }
  );

  const deleteVisitMutation = useMutation(
    (visitId) => serviceCallVisitsAPI.deleteVisit(serviceCall.id, visitId),
    {
      onSuccess: () => {
        toast.success('Visit deleted successfully');
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries('completedServiceCalls');
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete visit');
      }
    }
  );

  if (!isOpen || !serviceCall) return null;

  const fullServiceCall = serviceCallData?.data?.serviceCall || serviceCall;
  const client = fullServiceCall.client;
  const technician = fullServiceCall.technician;
  const visits = fullServiceCall.visits || [];
  const isCspire = !!fullServiceCall.isCspire;
  const isFranklin = !!fullServiceCall.isFranklin;
  const franklinSite = fullServiceCall.franklinSite;
  const cspireWorkOrder = fullServiceCall.cspireWorkOrder;
  const cspireSite = cspireWorkOrder?.site;
  const customerName = getServiceCallListCustomerName(fullServiceCall);

  const handleDownloadPDF = async () => {
    try {
      if (isCspire) {
        await downloadInvoicePdfForServiceCall(fullServiceCall.id, fullServiceCall.invoiceNumber);
      } else {
        await generateServiceCallPDF(fullServiceCall);
      }
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintPDF = async () => {
    try {
      if (isCspire) {
        await printInvoiceForServiceCall(fullServiceCall.id);
      } else {
        await printServiceCallPDF(fullServiceCall);
      }
      toast.success('Print dialog opened');
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast.error('Failed to print PDF');
    }
  };

  const handleFinalize = () => {
    if (window.confirm('Are you sure you want to mark this service call as finalized?')) {
      finalizeMutation.mutate();
    }
  };

  const handleEditVisit = (visit) => {
    setVisitToEdit(visit);
    setIsVisitEditModalOpen(true);
  };

  const handleDeleteVisit = (visit) => {
    if (window.confirm(`Are you sure you want to delete Visit #${visit.visitNumber}? This action cannot be undone.`)) {
      deleteVisitMutation.mutate(visit.id);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 no-print">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10 print-content">
        <div className="flex items-center justify-between mb-4 print:mb-6">
          <h3 className="text-lg font-medium text-gray-900 print:text-2xl">Service Call Details</h3>
          <div className="flex items-center space-x-2 no-print">
            {onEdit && (
              <button
                onClick={() => {
                  onEdit(fullServiceCall);
                }}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center space-x-1"
                title="Edit Service Call"
              >
                <Edit className="h-4 w-4" />
                <span>Edit</span>
              </button>
            )}
            {onReturnToTech && fullServiceCall.status === 'completed' && !fullServiceCall.isFinalized && (
              <button
                onClick={onReturnToTech}
                className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                Return to Tech
              </button>
            )}
            {fullServiceCall.status === 'completed' && !fullServiceCall.isFinalized && (
              <button
                onClick={handleFinalize}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Mark as Finalized
              </button>
            )}
            {fullServiceCall.isCspire && (
              <button
                onClick={() => setShowInvoiceEditor(true)}
                className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center space-x-1"
              >
                <FileText className="h-4 w-4" />
                <span>Edit Invoice</span>
              </button>
            )}
            <button
              onClick={handleDownloadPDF}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1"
            >
              <FileDown className="h-4 w-4" />
              <span>View PDF</span>
            </button>
            <button
              onClick={handlePrintPDF}
              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center space-x-1"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 print:space-y-3 max-h-[70vh] overflow-y-auto pr-2">
          {/* Client Information - Prominent at Top */}
          <div className="bg-primary-50 border border-primary-200 p-4 rounded-md">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-5 w-5 text-primary-700" />
              <h4 className="text-base font-bold text-primary-900">{customerName}</h4>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {isCspire ? (
                <>
                  <div>
                    <span className="text-primary-700 font-medium">Contact:</span>{' '}
                    <span className="text-gray-900">{cspireSite?.contactName || cspireWorkOrder?.openedByName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Phone:</span>{' '}
                    <span className="text-gray-900">{cspireSite?.contactPhone || cspireWorkOrder?.openedByPhone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Address:</span>{' '}
                    <span className="text-gray-900">{cspireSite?.addressLine1 || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Email:</span>{' '}
                    <span className="text-gray-900">{cspireSite?.contactEmail || cspireWorkOrder?.openedByEmail || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">City, State:</span>{' '}
                    <span className="text-gray-900">{[cspireSite?.city, cspireSite?.state, cspireSite?.zipCode].filter(Boolean).join(', ') || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Site ID:</span>{' '}
                    <span className="text-gray-900">{cspireSite?.siteId || 'N/A'}</span>
                  </div>
                </>
              ) : isFranklin && franklinSite ? (
                <>
                  <div>
                    <span className="text-primary-700 font-medium">Site ID:</span>{' '}
                    <span className="text-gray-900">{franklinSite.siteId || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Location:</span>{' '}
                    <span className="text-gray-900">{franklinSite.locationName || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Company:</span>{' '}
                    <span className="text-gray-900">{franklinSite.company || 'N/A'}</span>
                  </div>
                  {franklinSite.address && (
                    <div>
                      <span className="text-primary-700 font-medium">Address:</span>{' '}
                      <span className="text-gray-900">{franklinSite.address}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-primary-700 font-medium">City, State:</span>{' '}
                    <span className="text-gray-900">{[franklinSite.city, franklinSite.state, franklinSite.zipCode].filter(Boolean).join(', ') || 'N/A'}</span>
                  </div>
                  {franklinSite.backupPower && (
                    <div>
                      <span className="text-primary-700 font-medium">Backup Power:</span>{' '}
                      <span className="text-gray-900">{franklinSite.backupPower}</span>
                    </div>
                  )}
                  {franklinSite.tankSize && (
                    <div>
                      <span className="text-primary-700 font-medium">Tank Size:</span>{' '}
                      <span className="text-gray-900">{franklinSite.tankSize}</span>
                    </div>
                  )}
                  {franklinSite.generatorModel && (
                    <div>
                      <span className="text-primary-700 font-medium">Generator Model:</span>{' '}
                      <span className="text-gray-900">{franklinSite.generatorModel}</span>
                    </div>
                  )}
                  {franklinSite.generatorSerialNumber && (
                    <div>
                      <span className="text-primary-700 font-medium">Serial Number:</span>{' '}
                      <span className="text-gray-900">{franklinSite.generatorSerialNumber}</span>
                    </div>
                  )}
                  {franklinSite.lastServiceDate && (
                    <div>
                      <span className="text-primary-700 font-medium">Last Service:</span>{' '}
                      <span className="text-gray-900">{formatDate(franklinSite.lastServiceDate)}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <span className="text-primary-700 font-medium">Contact:</span>{' '}
                    <span className="text-gray-900">{`${client?.contactFirstName || ''} ${client?.contactLastName || ''}`.trim() || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Phone:</span>{' '}
                    <span className="text-gray-900">{client?.phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Address:</span>{' '}
                    <span className="text-gray-900">{client?.address || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">Email:</span>{' '}
                    <span className="text-gray-900">{client?.email || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-primary-700 font-medium">City, State:</span>{' '}
                    <span className="text-gray-900">{`${client?.city || ''}${client?.city && client?.state ? ', ' : ''}${client?.state || ''} ${client?.zipCode || ''}`.trim() || 'N/A'}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Quick CSpire site oil edit (visible for CSpire calls) */}
          {isCspire && cspireSite && (
            <CspireOilQuickEditPanel cspireSite={cspireSite} serviceCallId={fullServiceCall.id} />
          )}

          {/* Service Call Information */}
          <div className="bg-gray-50 p-4 rounded-md relative">
            <div className="flex items-start justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Service Call Information</h4>
              {fullServiceCall.invoiceNumber && (
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase">Invoice #</div>
                  <div className="text-lg font-bold text-gray-900">{fullServiceCall.invoiceNumber}</div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Status:</span>{' '}
                <span className={`status-badge ${getStatusColor(fullServiceCall.status)}`}>
                  {getStatusText(fullServiceCall.status)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Priority:</span>{' '}
                <span className={`status-badge ${getStatusColor(fullServiceCall.priority)}`}>
                  {getStatusText(fullServiceCall.priority)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Technician:</span> {technician ? `${technician.firstName} ${technician.lastName}` : 'Unassigned'}
              </div>
              {fullServiceCall.scheduledDate && (
                <div>
                  <span className="text-gray-600">Scheduled:</span> {formatDateTime(fullServiceCall.scheduledDate)}
                </div>
              )}
              {fullServiceCall.completedDate && (
                <div>
                  <span className="text-gray-600">Completed:</span> {formatDateTime(fullServiceCall.completedDate)}
                </div>
              )}
              {fullServiceCall.isFinalized && (
                <div>
                  <span className="text-gray-600">Finalized:</span> <span className="text-green-600 font-medium">Yes</span>
                </div>
              )}
              {fullServiceCall.type && (
                <div>
                  <span className="text-gray-600">Type:</span>{' '}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    fullServiceCall.type.name.toLowerCase() === 'visit'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {fullServiceCall.type.name}
                  </span>
                </div>
              )}
              {fullServiceCall.isWarranty && (
                <div>
                  <span className="text-gray-600">Warranty:</span> <span className="text-blue-600 font-medium">Yes</span>
                </div>
              )}
              {(fullServiceCall.needsTechInfo || fullServiceCall.techInfoUpdatedAt) && (
                <div>
                  <span className="text-gray-600">Tech Info:</span>{' '}
                  {fullServiceCall.needsTechInfo ? (
                    <span className="text-yellow-700 font-medium">Awaiting update</span>
                  ) : (
                    <span className="text-blue-700 font-medium">Updated</span>
                  )}
                </div>
              )}
            </div>

            {/* Correction Flag */}
            {fullServiceCall.isCorrectionFlag && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Correction Flag</span>
                </div>
                {fullServiceCall.correctionAtFaultTechnician && (
                  <p className="text-sm text-amber-800 mt-1">
                    <span className="font-medium">At fault:</span>{' '}
                    {fullServiceCall.correctionAtFaultTechnician.firstName}{' '}
                    {fullServiceCall.correctionAtFaultTechnician.lastName}
                  </p>
                )}
                {fullServiceCall.correctionNote && (
                  <p className="text-sm text-amber-700 mt-1">{fullServiceCall.correctionNote}</p>
                )}
              </div>
            )}
          </div>

          {(fullServiceCall.needsTechInfo || fullServiceCall.techInfoUpdatedAt) && fullServiceCall.techInfoRequestReason && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Tech Info Request</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.techInfoRequestReason}</p>
              {fullServiceCall.techInfoRequestedAt && (
                <p className="text-xs text-gray-600 mt-2">
                  Requested: {formatDateTime(fullServiceCall.techInfoRequestedAt)}
                </p>
              )}
              {fullServiceCall.techInfoUpdatedAt && (
                <p className="text-xs text-gray-600">
                  Updated: {formatDateTime(fullServiceCall.techInfoUpdatedAt)}
                </p>
              )}
            </div>
          )}

          {/* Visit History */}
          {visits.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <History className="h-5 w-5 text-yellow-700" />
                  <h4 className="font-semibold text-yellow-900">
                    Visit History ({visits.length} visit{visits.length !== 1 ? 's' : ''})
                  </h4>
                </div>
                {fullServiceCall.invoiceNumber && (
                  <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                    Invoice #{fullServiceCall.invoiceNumber}
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {visits.map((visit, idx) => (
                  <div key={visit.id} className="bg-white rounded-md p-3 border border-yellow-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{formatDateTime(visit.visitDate)}</span>
                        <button
                          onClick={() => handleEditVisit(visit)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Edit this visit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteVisit(visit)}
                          disabled={deleteVisitMutation.isLoading}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete this visit"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {visit.technician && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Technician:</span> {visit.technician.firstName} {visit.technician.lastName}
                      </p>
                    )}
                    {visit.type && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Type:</span> {visit.type.name}
                      </p>
                    )}
                    {visit.workPerformed && (
                      <div className="mb-2">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-gray-700">Work Performed:</p>
                          <CopyTextButton text={visit.workPerformed} size="xs" />
                        </div>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{visit.workPerformed}</p>
                      </div>
                    )}
                    {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                      <div className="bg-yellow-100 border border-yellow-300 rounded p-2 mb-2">
                        <p className="text-sm font-medium text-yellow-900">Return Reason:</p>
                        <p className="text-sm text-yellow-800">{visit.reasonForReturn}</p>
                      </div>
                    )}
                    {visit.partsUsed && visit.partsUsed.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">Parts Used:</p>
                        <div className="space-y-1">
                          {visit.partsUsed.map((part, partIdx) => {
                            const itemNumber = part.itemNumber || part.name || 'N/A';
                            const quantity = parseFloat(part.quantity) || 0;
                            const cost = parseFloat(part.cost) || 0;
                            return (
                              <div key={partIdx} className="text-xs text-gray-600 bg-gray-50 p-1 rounded">
                                {itemNumber} - Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(visit.laborHours || visit.generatorHours || visit.mileage || visit.fuelLevel) && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-600">
                        {visit.laborHours && (
                          <div>
                            <span className="font-medium">Labor:</span> {parseFloat(visit.laborHours).toFixed(2)}h
                          </div>
                        )}
                        {visit.generatorHours && (
                          <div>
                            <span className="font-medium">Gen Hours:</span> {parseFloat(visit.generatorHours).toFixed(2)}
                          </div>
                        )}
                        {visit.mileage && (
                          <div>
                            <span className="font-medium">Mileage:</span> {parseFloat(visit.mileage).toFixed(2)}
                          </div>
                        )}
                        {visit.fuelLevel && (
                          <div>
                            <span className="font-medium">Fuel Level:</span> {visit.fuelLevel}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generator Information */}
          {client && (client.generatorType || client.generatorModel || client.generatorSerialNumber || client.generatorSize || 
           client.generatorCoolingType || client.airFilter || client.fuelFilter1 || client.fuelFilter2 || client.oilFilter1 || client.oilFilter2 || 
           client.fuelType || client.oilType || client.oilAmount || client.fuelAdditive) && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
              <h4 className="font-semibold text-blue-900 mb-2">Generator Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {client.generatorType && (
                  <div>
                    <span className="text-blue-700 font-medium">Generator Type:</span> <span className="text-blue-900">{client.generatorType}</span>
                  </div>
                )}
                {client.generatorModel && (
                  <div>
                    <span className="text-blue-700 font-medium">Model:</span> <span className="text-blue-900">{client.generatorModel}</span>
                  </div>
                )}
                {client.generatorSerialNumber && (
                  <div>
                    <span className="text-blue-700 font-medium">Serial Number:</span> <span className="text-blue-900">{client.generatorSerialNumber}</span>
                  </div>
                )}
                {client.generatorSize && (
                  <div>
                    <span className="text-blue-700 font-medium">Size/Capacity:</span> <span className="text-blue-900">{client.generatorSize}</span>
                  </div>
                )}
                {client.generatorCoolingType && (
                  <div>
                    <span className="text-blue-700 font-medium">Cooling:</span> <span className="text-blue-900">{formatCoolingType(client.generatorCoolingType)}</span>
                  </div>
                )}
                {client.fuelType && (
                  <div>
                    <span className="text-blue-700 font-medium">Fuel Type:</span> <span className="text-blue-900">{client.fuelType}</span>
                  </div>
                )}
                {client.oilType && (
                  <div>
                    <span className="text-blue-700 font-medium">Oil Type:</span> <span className="text-blue-900">{client.oilType}</span>
                  </div>
                )}
                {client.oilAmount && (
                  <div>
                    <span className="text-blue-700 font-medium">Oil Amount:</span> <span className="text-blue-900">{client.oilAmount}</span>
                  </div>
                )}
                {client.fuelAdditive && (
                  <div>
                    <span className="text-blue-700 font-medium">Fuel Additive:</span> <span className="text-blue-900">{client.fuelAdditive}</span>
                  </div>
                )}
                {client.airFilter && (
                  <div>
                    <span className="text-blue-700 font-medium">Air Filter:</span> <span className="text-blue-900">{client.airFilter}</span>
                  </div>
                )}
                {client.fuelFilter1 && (
                  <div>
                    <span className="text-blue-700 font-medium">Fuel Filter 1:</span> <span className="text-blue-900">{client.fuelFilter1}</span>
                  </div>
                )}
                {client.fuelFilter2 && (
                  <div>
                    <span className="text-blue-700 font-medium">Fuel Filter 2:</span> <span className="text-blue-900">{client.fuelFilter2}</span>
                  </div>
                )}
                {client.oilFilter1 && (
                  <div>
                    <span className="text-blue-700 font-medium">Oil Filter 1:</span> <span className="text-blue-900">{client.oilFilter1}</span>
                  </div>
                )}
                {client.oilFilter2 && (
                  <div>
                    <span className="text-blue-700 font-medium">Oil Filter 2:</span> <span className="text-blue-900">{client.oilFilter2}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {fullServiceCall.description && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.description}</p>
            </div>
          )}

          {/* Work Performed */}
          {fullServiceCall.workPerformed && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="font-semibold text-gray-900">Work Performed</h4>
                <CopyTextButton text={fullServiceCall.workPerformed} />
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.workPerformed}</p>
            </div>
          )}

          {/* Parts Used */}
          {fullServiceCall.partsUsed && fullServiceCall.partsUsed.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Parts Used</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fullServiceCall.partsUsed.map((part, index) => {
                    const itemNumber = part.itemNumber || part.name || 'N/A';
                    const description = part.description || part.name || '';
                    const quantity = parseFloat(part.quantity) || 0;
                    const cost = parseFloat(part.cost) || 0;
                    const total = quantity * cost;
                    return (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{itemNumber}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{description}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{quantity.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">${cost.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">${total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Labor, Cost, Hours & Mileage */}
          {(fullServiceCall.laborHours || fullServiceCall.totalCost || fullServiceCall.generatorHours || fullServiceCall.mileage) && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold text-gray-900 mb-2">Service Metrics</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {fullServiceCall.laborHours && (
                  <div>
                    <span className="text-gray-600">Labor Hours:</span> {fullServiceCall.laborHours}
                  </div>
                )}
                {fullServiceCall.totalCost && (
                  <div>
                    <span className="text-gray-600">Total Cost:</span> ${parseFloat(fullServiceCall.totalCost).toFixed(2)}
                  </div>
                )}
                {fullServiceCall.generatorHours && (
                  <div>
                    <span className="text-gray-600">Generator Hours:</span> {parseFloat(fullServiceCall.generatorHours).toFixed(2)}
                  </div>
                )}
                {fullServiceCall.mileage && (
                  <div>
                    <span className="text-gray-600">Mileage:</span> {parseFloat(fullServiceCall.mileage).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Service Date */}
          {fullServiceCall.nextServiceDate && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold text-gray-900 mb-2">Next Service</h4>
              <div className="text-sm">
                <span className="text-gray-600">Scheduled for:</span> {formatDateTime(fullServiceCall.nextServiceDate)}
              </div>
            </div>
          )}

          {/* Customer Notes */}
          {fullServiceCall.customerNotes && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Customer Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.customerNotes}</p>
            </div>
          )}

          {/* Internal Notes */}
          {fullServiceCall.internalNotes && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Internal Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.internalNotes}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Visit Edit Modal */}
      {isVisitEditModalOpen && (
        <VisitEditModal
          visit={visitToEdit}
          serviceCallId={serviceCall.id}
          isOpen={isVisitEditModalOpen}
          isCspire={Boolean(fullServiceCall?.isCspire ?? serviceCall?.isCspire)}
          onClose={() => {
            setIsVisitEditModalOpen(false);
            setVisitToEdit(null);
          }}
        />
      )}
    </div>
    <CspireInvoiceEditor
      serviceCall={fullServiceCall}
      isOpen={showInvoiceEditor}
      onClose={() => setShowInvoiceEditor(false)}
    />
    </>
  );
};

export default function ServiceCalls() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [clientFilter, setClientFilter] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [warrantyFilter, setWarrantyFilter] = useState('');
  const [finalizedFilter, setFinalizedFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [serviceCallToEdit, setServiceCallToEdit] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortBy, setSortBy] = useState('clientName'); // Default sort by client name
  const [sortOrder, setSortOrder] = useState('ASC'); // Default A->Z
  const [selectedReturnServiceCall, setSelectedReturnServiceCall] = useState(null);
  const [isReturnToTechModalOpen, setIsReturnToTechModalOpen] = useState(false);
  const [isExportListModalOpen, setIsExportListModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch clients for filter dropdown
  const { data: clientsData } = useQuery(
    'clients-for-filter',
    () => clientsAPI.getClients({ limit: 1000, isActive: true }),
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );

  // Fetch technicians for filter dropdown
  const { data: techniciansData } = useQuery(
    'technicians-for-filter',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 }),
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );

  // Fetch service call types for filter dropdown
  const { data: typesData } = useQuery(
    'serviceCallTypes-for-filter',
    () => serviceCallTypesAPI.getServiceCallTypes({ includeInactive: false }),
    { staleTime: 5 * 60 * 1000 } // Cache for 5 minutes
  );

  // Build query params
  const queryParams = {
    page,
    limit: pageSize,
    sortBy,
    sortOrder,
    ...(searchTerm && { search: searchTerm }),
    ...(clientFilter && { clientId: clientFilter }),
    ...(technicianFilter && { technicianId: technicianFilter }),
    ...(statusFilter && { status: statusFilter }),
    ...(priorityFilter && { priority: priorityFilter }),
    ...(typeFilter && { typeId: typeFilter }),
    ...(warrantyFilter !== '' && { isWarranty: warrantyFilter === 'true' }),
    ...(finalizedFilter !== '' && { isFinalized: finalizedFilter }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    ...(segmentFilter && segmentFilter !== 'all' && { segment: segmentFilter }),
  };

  const exportListInitialFilters = useMemo(
    () => ({
      searchTerm,
      clientFilter,
      technicianIds: technicianFilter ? [technicianFilter] : [],
      statusFilter,
      priorityFilter,
      typeFilter,
      warrantyFilter,
      finalizedFilter,
      dateFrom,
      dateTo,
      dateRangeField: 'scheduled',
      segmentFilter,
      sortBy,
      sortOrder
    }),
    [
      searchTerm,
      clientFilter,
      technicianFilter,
      statusFilter,
      priorityFilter,
      typeFilter,
      warrantyFilter,
      finalizedFilter,
      dateFrom,
      dateTo,
      segmentFilter,
      sortBy,
      sortOrder
    ]
  );

  const { data, isLoading } = useQuery(
    ['serviceCalls', queryParams],
    () => serviceCallsAPI.getServiceCalls(queryParams),
    { keepPreviousData: true }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (serviceCallId) => serviceCallsAPI.deleteServiceCall(serviceCallId),
    {
      onSuccess: () => {
        toast.success('Service call deleted successfully');
        queryClient.invalidateQueries('serviceCalls');
        setSelectedIds(new Set());
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete service call');
      }
    }
  );

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation(
    (serviceCallIds) => serviceCallsAPI.bulkDeleteServiceCalls(serviceCallIds),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Service calls deleted successfully');
        queryClient.invalidateQueries('serviceCalls');
        setSelectedIds(new Set());
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete service calls');
      }
    }
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [clientFilter, technicianFilter, statusFilter, priorityFilter, typeFilter, warrantyFilter, finalizedFilter, dateFrom, dateTo, segmentFilter, pageSize]);

  // Reset selection when page or filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, clientFilter, technicianFilter, statusFilter, priorityFilter, typeFilter, warrantyFilter, finalizedFilter, dateFrom, dateTo, segmentFilter, searchTerm]);

  const pagination = data?.data?.pagination;
  const serviceCalls = data?.data?.serviceCalls || [];
  const clients = clientsData?.data?.clients || [];
  const technicians = techniciansData?.data?.users || [];
  const serviceCallTypes = typesData?.data?.types || [];

  // Debug: Log technicians data
  console.log('Technicians data:', {
    raw: techniciansData,
    extracted: technicians,
    filtered: technicians.filter((tech) => tech.isActive),
    count: technicians.length
  });

  const defaultStatusFilter = '';
  const defaultFinalizedFilter = '';

  const hasActiveFilters =
    clientFilter ||
    technicianFilter ||
    (statusFilter && statusFilter !== defaultStatusFilter) ||
    priorityFilter ||
    typeFilter ||
    warrantyFilter !== '' ||
    (finalizedFilter !== '' && finalizedFilter !== defaultFinalizedFilter) ||
    dateFrom ||
    dateTo ||
    (segmentFilter && segmentFilter !== 'all');

  const clearFilters = () => {
    setClientFilter('');
    setTechnicianFilter('');
    setStatusFilter(defaultStatusFilter);
    setPriorityFilter('');
    setTypeFilter('');
    setWarrantyFilter('');
    setFinalizedFilter(defaultFinalizedFilter);
    setDateFrom('');
    setDateTo('');
    setSegmentFilter('all');
    setPage(1);
  };

  const applyCompletedNotFinalizedFilter = () => {
    setStatusFilter('completed');
    setFinalizedFilter('false');
    setShowFilters(true);
    setPage(1);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(serviceCalls.map(sc => sc.id));
      setSelectedIds(newSelected);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const buildDeletePrompt = (serviceCall) => {
    const company = serviceCall.client?.companyName || 'this customer';
    const isMonthlyContractRow = !!(serviceCall.serviceMonth && serviceCall.serviceYear)
      && (serviceCall.client?.isContractClient ?? true);
    const status = serviceCall.status;

    if (isMonthlyContractRow && status === 'unassigned') {
      const monthName = new Date(serviceCall.serviceYear, serviceCall.serviceMonth - 1, 1)
        .toLocaleString('en-US', { month: 'long' });
      return `Delete the unassigned ${monthName} ${serviceCall.serviceYear} contract service call for ${company}?\n\n`
        + 'The client will reappear automatically on the 1st of their next cycle month when the monthly job runs again. '
        + 'If you instead want to skip or bump this month, use the Skip / Bump actions on the dashboard so the audit trail is preserved.';
    }

    if (status === 'completed') {
      return `Delete the COMPLETED service call for ${company}?\n\nThis removes a historical record and cannot be undone.`;
    }

    return `Are you sure you want to delete this service call for ${company}?`;
  };

  const handleDeleteOne = (serviceCall) => {
    if (window.confirm(buildDeletePrompt(serviceCall))) {
      deleteMutation.mutate(serviceCall.id);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) {
      toast.error('No service calls selected');
      return;
    }

    const selectedServiceCalls = (data?.data?.serviceCalls || []).filter(sc => selectedIds.has(sc.id));
    const monthlyContractRows = selectedServiceCalls.filter(sc =>
      sc.status === 'unassigned' && sc.serviceMonth && sc.serviceYear
    ).length;

    let prompt = `Are you sure you want to delete ${selectedIds.size} service call(s)? This action cannot be undone.`;
    if (monthlyContractRows > 0) {
      prompt += `\n\n${monthlyContractRows} of these are unassigned monthly contract calls. The clients will reappear on the 1st of their next cycle month when the monthly job runs.`;
    }

    if (window.confirm(prompt)) {
      bulkDeleteMutation.mutate(Array.from(selectedIds));
    }
  };

  const handleEdit = (serviceCall) => {
    setServiceCallToEdit(serviceCall);
    setIsEditModalOpen(true);
  };

  const handleReturnToTech = (serviceCall) => {
    setSelectedReturnServiceCall(serviceCall);
    setIsReturnToTechModalOpen(true);
  };

  const handleConfirmReturnToTech = async (reason) => {
    if (!selectedReturnServiceCall || !reason) return;

    try {
      await serviceCallsAPI.updateServiceCall(selectedReturnServiceCall.id, {
        needsTechInfo: true,
        techInfoRequestReason: reason,
        techInfoRequestedAt: new Date().toISOString(),
        techInfoUpdatedAt: null
      });
      toast.success('Request sent to technician!');
      queryClient.invalidateQueries('serviceCalls');
      queryClient.invalidateQueries(['serviceCall', selectedReturnServiceCall.id]);
      setIsReturnToTechModalOpen(false);
      setSelectedReturnServiceCall(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send request');
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // Set new column and default to ASC
      setSortBy(column);
      setSortOrder('ASC');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortOrder === 'ASC' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="hub-page-header flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1>Service Call Lookup</h1>
          <p>Search and review service call history across all statuses</p>
        </div>
        <button
          type="button"
          onClick={() => setIsExportListModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <List className="h-4 w-4 mr-2" />
          Print / export list
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by customer name, Franklin location name or site ID, C Spire WO# or site..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="input pl-10"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-sm text-gray-700 hover:text-gray-900"
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
              {hasActiveFilters && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 text-primary-800 rounded-full">
                  Active
                </span>
              )}
            </button>
            <div className="flex items-center space-x-3">
              <button
                onClick={applyCompletedNotFinalizedFilter}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Completed Not Finalized
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center text-sm text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-4 border-t border-gray-200">
              {/* Client Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Customer
                </label>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Customers</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.companyName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Technician Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Technician
                </label>
                <select
                  value={technicianFilter}
                  onChange={(e) => setTechnicianFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Technicians</option>
                  {technicians.map((tech) => (
                    <option key={tech.id} value={tech.id}>
                      {tech.firstName} {tech.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="unassigned">Unassigned (monthly)</option>
                  <option value="queued">Queued (repair)</option>
                  <option value="assigned">Assigned</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Types</option>
                  {serviceCallTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Warranty Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Warranty
                </label>
                <select
                  value={warrantyFilter}
                  onChange={(e) => setWarrantyFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All</option>
                  <option value="true">Warranty</option>
                  <option value="false">Non-Warranty</option>
                </select>
              </div>

              {/* Finalized Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Finalized
                </label>
                <select
                  value={finalizedFilter}
                  onChange={(e) => setFinalizedFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All</option>
                  <option value="true">Finalized</option>
                  <option value="false">Not Finalized</option>
                </select>
              </div>

              {/* Account / site type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account / site type
                </label>
                <select
                  value={segmentFilter}
                  onChange={(e) => {
                    setSegmentFilter(e.target.value);
                    setPage(1);
                  }}
                  className="input"
                >
                  <option value="all">All</option>
                  <option value="cspire">C Spire only</option>
                  <option value="franklin">Franklin only</option>
                  <option value="commercial">Commercial only</option>
                  <option value="residential">Residential only</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Technician Filter Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-2 px-4 overflow-x-auto" aria-label="Technician filters">
            <button
              onClick={() => setTechnicianFilter('')}
              className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                technicianFilter === ''
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Technicians
              {technicianFilter === '' && serviceCalls.length > 0 && (
                <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 text-blue-600">
                  {pagination?.total || 0}
                </span>
              )}
            </button>
            {technicians.map((tech) => {
                const isActive = technicianFilter === tech.id;
                return (
                  <button
                    key={tech.id}
                    onClick={() => setTechnicianFilter(tech.id)}
                    className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tech.firstName} {tech.lastName}
                    {isActive && serviceCalls.length > 0 && (
                      <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 text-blue-600">
                        {pagination?.total || 0}
                      </span>
                    )}
                  </button>
                );
              })}
          </nav>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-blue-900">
                {selectedIds.size} service call{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="h-4 w-4" />
              <span>{bulkDeleteMutation.isLoading ? 'Deleting...' : 'Delete Selected'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Service Calls Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {serviceCalls.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">
              No service calls found matching your criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={serviceCalls.length > 0 && serviceCalls.every(sc => selectedIds.has(sc.id))}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('clientName')}
                  >
                    <div className="flex items-center">
                      Customer Name
                      {getSortIcon('clientName')}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('completedDate')}
                  >
                    <div className="flex items-center">
                      Date
                      {getSortIcon('completedDate')}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tech
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serviceCalls.map((serviceCall) => (
                  <tr key={serviceCall.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(serviceCall.id)}
                        onChange={() => handleSelectOne(serviceCall.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getServiceCallListCustomerName(serviceCall)}
                        {serviceCall.isCorrectionFlag && (
                          <span
                            className="ml-2 inline-flex items-center"
                            title={
                              [
                                serviceCall.correctionAtFaultTechnicianId
                                  && serviceCall.correctionAtFaultTechnician
                                  ? `At fault: ${serviceCall.correctionAtFaultTechnician.firstName} ${serviceCall.correctionAtFaultTechnician.lastName}`
                                  : null,
                                serviceCall.correctionNote
                              ].filter(Boolean).join(' — ') || 'Correction flag'
                            }
                          >
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {serviceCall.completedDate 
                          ? formatDate(serviceCall.completedDate)
                          : serviceCall.scheduledDate 
                            ? formatDate(serviceCall.scheduledDate)
                            : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {serviceCall.type ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            serviceCall.type.name.toLowerCase() === 'service'
                              ? 'bg-blue-100 text-blue-800'
                              : serviceCall.type.name.toLowerCase() === 'repair' || 
                                serviceCall.type.name.toLowerCase() === 'repair and service' ||
                                serviceCall.type.name.toLowerCase() === 'inspection and repair'
                              ? 'bg-red-100 text-red-800'
                              : serviceCall.type.name.toLowerCase() === 'inspection'
                              ? 'bg-green-100 text-green-800'
                              : serviceCall.type.name.toLowerCase() === 'warranty'
                              ? 'bg-purple-100 text-purple-800'
                              : serviceCall.type.name.toLowerCase() === 'visit'
                              ? 'bg-orange-100 text-orange-800'
                              : serviceCall.type.name.toLowerCase() === 'start up'
                              ? 'bg-cyan-100 text-cyan-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {serviceCall.type.name}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {serviceCall.technician
                          ? `${serviceCall.technician.firstName} ${serviceCall.technician.lastName}`
                          : 'Unassigned'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const rd = getResolutionDisplay(serviceCall);
                        if (rd) {
                          return (
                            <span className={`status-badge ${rd.color}`}>{rd.label}</span>
                          );
                        }
                        return (
                          <span className={`status-badge ${getStatusColor(serviceCall.status)}`}>
                            {getStatusText(serviceCall.status)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {serviceCall.invoiceNumber || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        <button 
                          onClick={() => {
                            setSelectedServiceCall(serviceCall);
                            setIsDetailsModalOpen(true);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          title="View details"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleEdit(serviceCall)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit service call"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOne(serviceCall)}
                          disabled={deleteMutation.isLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete service call"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total > 0 && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Results Info */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700">
                Showing {pagination.start} to {pagination.end} of {pagination.total} results
              </div>
              
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="input input-sm py-1 px-2 text-sm"
                >
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
            </div>

            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={pagination.page === 1}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={!pagination.hasPrev}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
              
              <div className="flex items-center gap-2 px-3">
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.pages}
                </span>
              </div>

              <button
                onClick={() => setPage(page + 1)}
                disabled={!pagination.hasNext}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
              <button
                onClick={() => setPage(pagination.pages)}
                disabled={pagination.page === pagination.pages}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Call Details Modal */}
      <ServiceCallDetailsModal
        serviceCall={selectedServiceCall}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedServiceCall(null);
        }}
        onEdit={(serviceCall) => {
          setServiceCallToEdit(serviceCall);
          setIsEditModalOpen(true);
        }}
        onReturnToTech={() => handleReturnToTech(selectedServiceCall)}
      />

      {/* Service Call Edit Modal */}
      <ServiceCallEditModal
        serviceCall={serviceCallToEdit}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setServiceCallToEdit(null);
        }}
      />

      {/* Return to Tech Modal */}
      <ReturnToTechModal
        serviceCall={selectedReturnServiceCall}
        isOpen={isReturnToTechModalOpen}
        onClose={() => {
          setIsReturnToTechModalOpen(false);
          setSelectedReturnServiceCall(null);
        }}
        onConfirm={handleConfirmReturnToTech}
      />

      <ServiceCallListExportModal
        isOpen={isExportListModalOpen}
        onClose={() => setIsExportListModalOpen(false)}
        initialFilters={exportListInitialFilters}
        technicians={technicians}
        clients={clients}
        serviceCallTypes={serviceCallTypes}
      />
      </div>
    </div>
  );
}
